import { v4 as uuidv4 } from 'uuid';
import { mapClaimingFor } from './sharedMapper';
import { stepOtherExpenseLookup as expenseLookup } from './claimCodeMapper';
import { mapToOtherExpense } from './expenseMapper';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire(data, claimCode) {
  const questionnaire = [];
  const entries = Object.entries(data);
  const excludedEntries = ['personalLiabilitiesExpensesClaimed',
    'golfPackOptionalExpensesClaimed',
    'bicyclePackOptionalExpensesClaimed',
    'bicyclePackOptionalClaimDetail',
    'golfPackOptionalClaimDetail',
    'personalLiabilitiesClaimDetail',
    'anythingElse',
    'anythingElseExpensesClaimed',
    'checkNameOfDeceased',
    'whatExpensesAreYouClaimingFor',
    'infoPersonalLiabilityItems',
    'whatAreYourExpensesRelatedTo',
    'infoLossOfIncomeItems',
    'infoTotalPermanentDisablementItems',
    'infoLossOfLifeItems',
    'infoPersonalAccidentItems',
  ];
  entries.forEach(([property, value]) => {
    if (excludedEntries.indexOf(property) < 0) {
      questionnaire.push({
        question: `section10.${property}`,
        answer: value,
        claimTypeCode: claimCode,
      });
    }
  });

  const personalLiabilitiesClaimDetail = mapExpenseDetailToQuestionnaire('personalLiabilitiesClaimDetail', data.personalLiabilitiesClaimDetail, expenseLookup.areYouClaimingForPersonalLiability.claimCode);
  const anythingElse = mapExpenseDetailToQuestionnaire('anythingElse', data.anythingElse, expenseLookup.isThereAnyThingElseYouWouldLikeToClaimFor.claimCode);

  return [...questionnaire, ...personalLiabilitiesClaimDetail, ...anythingElse];
}

function getGroupId(item) {
  const selectedClaimForArray = item.selectedClaimFor
    ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor
    : [];
  return selectedClaimForArray.length > 1 ? uuidv4() : null;
}

function mapToExpenses(data) {
  const personalLiabilityExpense = mapToPersonalLiabilitiesExpense(data);
  const anythingElsePackOptionalExpense = mapIsThereAnyThingElseYouWouldLikeToClaimFor(data);
  const accidentalDeathExpenses = mapToAccidentalDeathDismembermentExpense(data);
  const repatriationExpenses = mapToForRepatriationExpenses(data);
  const burialExpenses = mapToForBurialExpenses(data);
  const personalLiabilityExpenses = mapToForPersonalLiability(data);
  const personalAccidentExpenses = mapToForPersonalAccident(data);

  return [...personalLiabilityExpense, ...anythingElsePackOptionalExpense, ...accidentalDeathExpenses, ...repatriationExpenses, ...burialExpenses, ...personalLiabilityExpenses, ...personalAccidentExpenses];
}

function mapToPersonalLiabilitiesExpense(data) {
  if (data.areYouClaimingForPersonalLiability === 'true') {
    const claimCode = expenseLookup.areYouClaimingForPersonalLiability;
    return data.personalLiabilitiesExpensesClaimed.map(mapToOtherExpense, claimCode);
  }

  return [];
}

function mapToAccidentalDeathDismembermentExpense(data) {
  if (data.areYouClaimingForAccidentalDeathAndDismemberment !== 'true' || !data.infoADAndDItems || data.infoADAndDItems.length < 1) {
    return [];
  }

  const expenses = [];
  const claimCode = expenseLookup.areYouClaimingForAccidentalDeathAndDismemberment.claimCode;

  if (data.infoADAndDItems.length > 0) {
    data.infoADAndDItems.forEach(
      (item) => {
        const groupId = getGroupId(item);
        const expense = mapExpenseADAndDItem(item, data.dateOfInjury, claimCode, groupId);
        if (!!expense) {
          expenses.push(expense);
        }
      }
    );
  }

  return expenses;
}

function mapToAccidentalDeathDismembermentExpenseLegacy(data) {
  if (data.areYouClaimingForAccidentalDeathAndDismemberment === 'true') {
    const claimCode = expenseLookup.areYouClaimingForAccidentalDeathAndDismemberment.claimCode;
    return [{
      shortDescription: `${data.nameOfDeceased} (AD&D)`,
      dateOfExpense: data.dateOfInjury,
      clientCost: 25000,
      currency: 'USD',
      rate: null,
      requestedAmount: null,
      claimTypeCode: claimCode,
    }];
  }

  return [];
}

function mapToForRepatriationExpenses(data) {
  if (data.areYouClaimingForRepatriationExpenses === 'true') {
    const claimCode = expenseLookup.areYouClaimingForRepatriationExpenses.claimCode;

    return [{
      shortDescription: data.causeOfDeath,
      dateOfExpense: data.dateOfDeath,
      clientCost: data.amountClaimed,
      currency: data.amountClaimedCurrency,
      rate: null,
      requestedAmount: null,
      claimTypeCode: claimCode,
    }];
  }

  return [];
}

function mapToForBurialExpenses(data) {
  if (data.areYouClaimingForBurialExpenses === 'true') {
    const claimCode = expenseLookup.areYouClaimingForBurialExpenses.claimCode;

    return [{
      shortDescription: data.causeOfDeathForBurial,
      dateOfExpense: data.dateOfDeathForBurial,
      clientCost: data.amountClaimedForBurial,
      currency: data.amountClaimedCurrencyForBurial,
      rate: null,
      requestedAmount: null,
      claimTypeCode: claimCode,
    }];
  }

  return [];
}

function mapToForPersonalLiability(data) {
  const infoPersonalLiabilityItems = data.infoPersonalLiabilityItems || [];

  if (infoPersonalLiabilityItems.length < 1) {
    return [];
  }
  const claimCode = expenseLookup.personalLiability.claimCode;
  const expenses = [];

  infoPersonalLiabilityItems.forEach(
    (item) => {
      const groupId = getGroupId(item);
      const expense = mapExpenseItem(item, data, claimCode, groupId);
      if (!!expense) {
        expenses.push(expense);
      }
    }
  );

  return expenses;
}

function mapToForPersonalAccident(data) {
  const infoPersonalAccidentItems = data.infoPersonalAccidentItems || [];
  const infoLossOfIncomeItems = data.infoLossOfIncomeItems || [];
  const infoTotalPermanentDisablementItems = data.infoTotalPermanentDisablementItems || [];
  const infoLossOfLifeItems = data.infoLossOfLifeItems || [];
  if (infoPersonalAccidentItems.length < 1 && infoLossOfIncomeItems.length < 1 && infoTotalPermanentDisablementItems.length < 1 && infoLossOfLifeItems.length < 1) {
    return [];
  }

  let claimCode = expenseLookup.personalAccident.claimCode;
  const expenses = [];

  if (infoPersonalAccidentItems.length > 0) {
    infoPersonalAccidentItems.forEach(
      (item) => {
        const groupId = getGroupId(item);
        const expense = mapExpenseItem(item, data, claimCode, groupId);
        if (!!expense) {
          expenses.push(expense);
        }
      }
    );
  }

  if (infoLossOfIncomeItems.length > 0) {
    claimCode = expenseLookup.lossOfIncome.claimCode;
    infoLossOfIncomeItems.forEach(
      (item) => {
        const groupId = getGroupId(item);
        const expense = mapExpenseItem(item, data, claimCode, groupId);
        if (!!expense) {
          expenses.push(expense);
        }
      }
    );
  }

  if (infoTotalPermanentDisablementItems.length > 0) {
    claimCode = expenseLookup.totalPermanentDisablement.claimCode;
    infoTotalPermanentDisablementItems.forEach(
      (item) => {
        const groupId = getGroupId(item);
        const expense = mapExpenseItem(item, data, claimCode, groupId);
        if (!!expense) {
          expenses.push(expense);
        }
      }
    );
  }

  if (infoLossOfLifeItems.length > 0) {
    claimCode = expenseLookup.lossOfLife.claimCode;
    infoLossOfLifeItems.forEach(
      (item) => {
        const groupId = getGroupId(item);
        const expense = mapExpenseItem(item, data, claimCode, groupId);
        if (!!expense) {
          expenses.push(expense);
        }
      }
    );
  }

  return expenses;
}

function mapIsThereAnyThingElseYouWouldLikeToClaimFor(data) {
  if (data.isThereAnyThingElseYouWouldLikeToClaimFor === 'true') {
    const claimCode = expenseLookup.isThereAnyThingElseYouWouldLikeToClaimFor;
    return data.anythingElseExpensesClaimed.map(mapToOtherExpense, claimCode);
  }

  return [];
}
function mapExpenseDetailToQuestionnaire(expensePrefix, expenseDetail, claimCode) {
  const questionnaire = [];
  const entries = Object.entries(expenseDetail);
  entries.forEach(([property, value]) => {
    questionnaire.push({
      question: `section10.${expensePrefix}.${property}`,
      answer: (property === 'claimingFor') ? mapClaimingFor(value) : value,
      claimTypeCode: claimCode,
    });
  });

  return questionnaire;
}

function mapExpenseItem(item, itemDate, claimCode, groupId) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    assessorValue: item.amountClaimed,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    groupId,
  };
}

function mapExpenseADAndDItem(item, itemDate, claimCode, groupId) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountClaimed || !item.currency) {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem}`,
    dateOfExpense: itemDate,
    clientCost: item.amountClaimed,
    currency: item.currency,
    rate: null,
    requestedAmount: null,
    assessorValue: item.amountClaimed,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    groupId,
  };
}

function mapReimbursement(item, formData, claimCode, groupId) {
  const selectedClaimForArray = item.selectedClaimFor ? (!Array.isArray(item.selectedClaimFor)) ? item.selectedClaimFor.toJS() : item.selectedClaimFor : [];
  const claimForInsuredIds = selectedClaimForArray.map((claimFor) => claimFor.policyRoleID);
  if (!item || !item.amountCreditedOrRefunded || !item.currencyCreditedOrRefunded || item.haveYouReceivedAnyCreditOrRefundFromTheServiceProvider !== 'true') {
    return null;
  }
  return {
    shortDescription: `${item.descriptionOfItem} (Reimbursement)`,
    dateOfExpense: item.dateOfExpenses,
    clientCost: `${item.amountCreditedOrRefunded * -1}`,
    currency: item.currencyCreditedOrRefunded,
    rate: null,
    requestedAmount: null,
    assessorValue: `${item.amountCreditedOrRefunded * -1}`,
    claimTypeCode: claimCode,
    claimForInsuredIds,
    claimForInsureds: selectedClaimForArray,
    groupId,
  };
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepOtherDataMapper.js